// internal-deps
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { CrossSellTabType, WidgetInfo, PriceInfo } from "@swiggy-private/cart-types";

// constants
import { SCREEN_NAME, IMPRESSION_ACTIONS, CLICK_ACTIONS } from "../constants";

export type { WidgetInfo, PriceInfo };

export interface CarouselItemDetailsParams {
    itemId: string;
    cartId: string;
    selectedTab?: CrossSellTabType | null;
    isCustomizable?: boolean;
    position?: string;
}

export interface ImpressionCrossSellM2ItemParams {
    item: string;
    cart: string;
    basePrice?: number;
    slashedPrice?: number;
    position?: string;
}

export interface ClickItemDetailsCarouselParams {
    itemId: string;
    cartId: string;
    position: string;
    selectedTab?: CrossSellTabType | null;
    priceInfo?: PriceInfo;
}

export interface ImpressionAddItemCarouselParams {
    items: string[];
    cartId: string;
    selectedTab?: CrossSellTabType | null;
    widgetInfo?: WidgetInfo;
}

export interface ClickAddItemCarouselParams {
    itemId: string;
    cartId: string;
    isCustomizable: boolean;
    selectedTab?: CrossSellTabType | null;
    position?: string;
    priceInfo?: PriceInfo;
}

export const impressionAddItemCarousel = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { items, cartId, selectedTab, widgetInfo }: ImpressionAddItemCarouselParams,
): void => {
    const context: Record<string, unknown> = {
        items,
        cartId,
        itemTab: selectedTab,
        ...(widgetInfo && {
            locked: !widgetInfo.isWidgetUnlocked,
            timer: widgetInfo.timer,
            distanceToUnlock: widgetInfo.distanceToUnlock,
            eoShown: widgetInfo.eoShown,
            eoMsg: widgetInfo.eoMsg,
        }),
    };

    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify(context),
        action: IMPRESSION_ACTIONS.IMPRESSION_ADD_ITEM_CAROUSEL,
    });
};

export const clickChangeTabCarousel = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId: string,
    selectedTab: CrossSellTabType | null,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ itemTab: selectedTab, cartId }),
        action: CLICK_ACTIONS.CLICK_CROSS_SELL_CHANGE_TAB,
    });
};

export const clickAddItemCarousel = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { itemId, cartId, isCustomizable, selectedTab, position, priceInfo }: ClickAddItemCarouselParams,
): void => {
    const context: Record<string, unknown> = {
        itemId,
        cartId,
        isCustomizable,
        itemTab: selectedTab,
        position,
        ...(priceInfo && {
            basePrice: priceInfo.basePrice,
            slashedPrice: priceInfo.slashedPrice,
        }),
    };

    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify(context),
        action: CLICK_ACTIONS.CLICK_ADD_ITEM_CAROUSEL,
    });
};

export const clickItemDetailsCarousel = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: ClickItemDetailsCarouselParams,
): void => {
    const { itemId, cartId, position, selectedTab, priceInfo } = params;
    const context: Record<string, unknown> = {
        itemId,
        cartId,
        position,
        itemTab: selectedTab,
        ...(priceInfo && {
            basePrice: priceInfo.basePrice,
            slashedPrice: priceInfo.slashedPrice,
        }),
    };

    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify(context),
        action: CLICK_ACTIONS.CLICK_ITEM_DETAILS_CAROUSEL,
    });
};

export const impressionCrossSellM2Item = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { item, cart, basePrice, slashedPrice, position }: ImpressionCrossSellM2ItemParams,
): void => {
    const context: Record<string, unknown> = {
        item,
        cart,
        basePrice,
        slashedPrice,
        position,
    };

    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify(context),
        action: IMPRESSION_ACTIONS.IMPRESSION_CAROUSEL_ITEM,
    });
};

export const impressionCarouselItemDetailsPopup = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { itemId, cartId, position, selectedTab }: CarouselItemDetailsParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ itemId, cartId, position, itemTab: selectedTab }),
        action: IMPRESSION_ACTIONS.IMPRESSION_CAROUSEL_ITEM_DETAILS_POPUP,
    });
};

export const impressionCartItemDetails = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
    itemId: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId, itemId }),
        action: IMPRESSION_ACTIONS.IMPRESSION_CART_ITEM_DETAILS,
    });
};

export const impressionMagicItemsFtue = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId }),
        action: IMPRESSION_ACTIONS.IMPRESSION_MAGIC_ITEMS_FTUE,
    });
};

export interface ImpressionMagicItemsTimerParams {
    cartId: string;
    nearExpiry: boolean;
    timerShown: boolean;
}

export const impressionMagicItemsTimer = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { cartId, nearExpiry, timerShown }: ImpressionMagicItemsTimerParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId, nearExpiry, timerShown }),
        action: IMPRESSION_ACTIONS.IMPRESSION_MAGIC_ITEMS_TIMER,
    });
};

export const impressionMagicItemsEmpty = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
    savingsText?: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId }),
        action: IMPRESSION_ACTIONS.IMPRESSION_MAGIC_ITEMS_EMPTY,
        label: savingsText,
    });
};

export const impressionMagicItemsUnlockPopup = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId }),
        action: IMPRESSION_ACTIONS.IMPRESSION_MAGIC_ITEMS_UNLOCK_POPUP,
    });
};

export const clickDismissMagicItemsUnlockPopup = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartId: string,
): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId }),
        action: CLICK_ACTIONS.CLICK_DISMISS_MAGIC_ITEMS_UNLOCK_POPUP,
    });
};

export const clickExploreMenuMagicItems = (clickEvent: AnalyticsContextValue["clickEvent"], cartId: string): void => {
    clickEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId }),
        action: CLICK_ACTIONS.CLICK_EXPLORE_MENU_MAGIC_ITEMS,
    });
};

export interface ImpressionMagicItemsOfferLimitParams {
    cartId: string;
    itemId: string;
    qty: number;
}

export const impressionMagicItemsOfferLimit = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { cartId, itemId, qty }: ImpressionMagicItemsOfferLimitParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId, itemId, qty }),
        action: IMPRESSION_ACTIONS.IMPRESSION_MAGIC_ITEMS_OFFER_LIMIT,
    });
};

export const magicItemsErrorCroutonImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartId: string,
    errorText: string,
): void => {
    impressionEvent({
        category: SCREEN_NAME.CHECKOUT,
        context: JSON.stringify({ cartId, errorText }),
        action: IMPRESSION_ACTIONS.IMPRESSION_MAGIC_ITEMS_ERROR_CROUTON,
    });
};
